import React, { Fragment, useCallback, useState, memo, useRef, useEffect } from 'react';
import { createSelector } from 'reselect';
import styled from 'styled-components';
import { getMeasurableFields } from '../../lib/exporterHelper';

import {
  calculatePagePercentageAdjustments,
  getPercentageAdjustedValue,
  getPageFieldPositions,
} from '../../lib/documentHelper';

import { getAutoHeightPositionChanges } from '../../lib/autoHeightHelper';

import FieldModes from '../../constants/FieldModes';

import GridLayout from './GridLayout';
import FieldCreationDropLayer from './FieldCreationDropLayer';

const StyledWrapper = styled.div`
  width: ${(props) => props.width}px;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledBorderWrapper = styled.div`
  display: flex;
  flex: 1;
  border: ${(props) => props.border};
  padding: ${(props) => props.padding || 0}px;
  margin: ${(props) => props.margin || 0}px;
  min-height: ${(props) => props.height}px;
`;

const StyledPageFields = styled.div`
  display: flex;
  flex: 1;
`;

const FormPage = (props) => {

  const {

    id,

    mode,
    licenseValidation,

    //Container properties
    containerWidth = 816,
    containerHeight = 1056,

    //Page Properties
    width = 816,
    cols,
    rowHeight,
    presentation,
    margin,
    padding,
    borderWidth,
    borderColor,
    borderStyle,

    fields,

    onFieldCreate,
    onFieldAutoHeightChange,

    /**
     * Page fieldPosition measurements
     */
    measure,
    updateTimestamps,
    measurements,
    onMeasurementsCompleted,

    enableLazyLoadFields,

    mockTestDndMonitor,

    config,

    onScrollContainerReady,

  } = props;

  const [scrollManagerRef, setScrollManagerRef] = useState(null);
  const [scrollUpdated, setScrollUpdated] = useState(0);
  const measurementsRef = useRef({});
  const pendingMeasurementsRef = useRef({});

  /**
   * The auto-height handler below is handed to a memoised component, so it has to keep the same
   * identity across renders or every field re-renders on every keystroke. Reading the fields
   * through a ref is what lets the callback have an empty dependency list and still see the
   * current document.
   */
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  // Expose scroll container to parent for programmatic navigation
  useEffect(() => {
    if (scrollManagerRef && onScrollContainerReady) {
      onScrollContainerReady(scrollManagerRef);
    }
  }, [scrollManagerRef, onScrollContainerReady]);

  /**
   * Handle container max width and height adjustments
   *
   * The page margin, padding, and border should be applied to the container and
   * reduce the max dimensions of the container element.
   *
   * Why is this? The container element determines the max dimensions that the
   * fields and PDF background can be within. The border, padding, and margin of
   * the page itself reduce that max dimension limit. Therefore we apply those
   * page properties to the container since that is what enforces those dimensions
   * for the page fields and PDF background.
   *
   * For instance if the container max width is 816px and the page has padding of
   * 4px (x2 for both sides) then the max width of the container should be reduced
   * to 804px. Then we can scale the PDF background and fields to fit within that
   * new 804px width limit.
   */
  const adjustedContainerWidth = getAdjustedDimension(containerWidth, padding, margin, borderWidth);
  const adjustedContainerHeight = getAdjustedDimension(containerHeight, padding, margin, borderWidth);

  /**
   * Why do we have duplicate arguments for width? With e-forms we only need to scale by width. We do not
   * need to worry about height because height is not restricted in e-form format. We pass width twice so
   * that we get the same percentage difference and always have the width adjustment percentage given to us.
   */
  const percentageDifference = calculatePagePercentageAdjustments(width, width, adjustedContainerWidth, adjustedContainerWidth);

  /**
   * Why -18 from the page width? This is to adjust the react-grid-layout away from
   * the scrollbar gutter width. This way when the scrollbar appears due to overflow
   * the field drag and resize icons wont be covered up by the scollbar.
   */
  const scrollbarGutterWidth = 18;
  const adjustedPageWidth = getPercentageAdjustedValue(width, percentageDifference) - scrollbarGutterWidth;

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const pageBorderWidth = borderWidth !== undefined ? `${borderWidth}px` : '0px';
  const pageBorderColor = borderColor || 'transparent';
  const pageBorderStyle = borderStyle || 'solid';
  const pageBorder = `${pageBorderWidth} ${pageBorderStyle} ${pageBorderColor}`;

  /**
   * Start Field Helpers
   */
  const FieldCreationDropLayerElement = mode === FieldModes.edit ? FieldCreationDropLayer : Fragment;

  const dropElementProps = {};

  if (mode === FieldModes.edit) {

    dropElementProps.pageId = id;
    dropElementProps.pageWidth = width;
    dropElementProps.pageColumns = cols;
    dropElementProps.pageRowHeight = rowHeight;
    dropElementProps.onDrop = onFieldCreate;
    dropElementProps.mockTestDndMonitor = mockTestDndMonitor;

  }

  const handleFieldMeasurementCompleted = useCallback((fieldId, fieldIdentifier, fieldPositionId, rect) => {

    /**
     * Handle measured height
     *
     * Convert to proper react-grid-layout row height.
     *
     * Why +4? The field has a transparent border of 1px all around
     * and 1px padding all around that is not accounted for in the
     * internal height calculation.
     */
    const measuredHeight = (rect.height + 4) / rowHeight;
    const nextRect = { ...rect, width: Math.ceil(rect.width + 4), height: Math.ceil(measuredHeight) };

    onMeasurementsCompleted(id, fieldId, fieldIdentifier, fieldPositionId, nextRect);
  }, []);

  /**
   * The auto-resize measurement overrides, read through a ref for the same reason fields are:
   * the handler below keeps its identity, so a direct read would see whatever map existed when
   * it was created. (measurementsRef above is a different, older ref; this one is not it.)
   */
  const autoResizeMeasurementsRef = useRef(measurements);
  autoResizeMeasurementsRef.current = measurements;

  /**
   * A field has told us how much room its value needs. Turn that into a new persisted height,
   * and tell the field what became of it.
   *
   * Returns one of three answers, which the field uses to decide whether its edit is spent:
   *   'applied'  a height change was sent
   *   'settled'  nothing to change (the box fits, or nothing could ever apply)
   *   'retry'    the measurement did not describe a settled box; measure again once it has
   *
   * The fieldPosition is looked up in the raw fields prop rather than in layoutFields, because
   * layoutFields carries the auto-resize measurement overrides: growing against a height the
   * document does not contain would persist a number nobody asked for.
   *
   * snapToRows is true here because this is a grid page. Its boxes and line heights are whole
   * pixels, so whole rows always cover a measured need exactly.
   */
  const handleFieldAutoHeightMeasured = useCallback((fieldId, fieldIdentifier, fieldPositionId, measurement) => {

    if (!onFieldAutoHeightChange) return 'settled';

    const fieldPosition = (fieldsRef.current || []).find((field) => field._id === fieldPositionId);

    if (!fieldPosition) return 'settled';

    /**
     * With autoResizeFields on, the drawn height is a measurement this feature owns, not the
     * document's, so no blur measurement can ever match it. Those blurs were always dropped;
     * they are still dropped, and not retried.
     */
    const overrides = autoResizeMeasurementsRef.current;

    if (overrides && overrides[fieldPositionId]) return 'settled';

    /**
     * Measured against a box the document no longer describes. This happens when two blur
     * events reach the same field for one loss of focus and the first has already changed it,
     * or when something else re-sized the field between the blur and this call. Applying the
     * old measurement to the new height would be wrong, so the field is asked to measure again.
     */
    if (Number.isFinite(measurement.heightAtMeasure) && measurement.heightAtMeasure !== fieldPosition.height) return 'retry';

    /**
     * A missing rowHeight means react-grid-layout's own default of 8 is what is being drawn, so
     * that is what the arithmetic has to use. A saved 0 or a negative value is passed straight
     * through: the renderer receives it too, and the helper's own rowHeight > 0 guard then
     * declines the malformed document rather than growing against a row size nothing is using.
     */
    const effectiveRowHeight = rowHeight === undefined ? 8 : rowHeight;

    const { changes, reason } = getAutoHeightPositionChanges({
      fieldPosition,
      measurement,
      snapToRows: true,
      rowHeight: effectiveRowHeight,
    });

    if (!changes) return reason;

    onFieldAutoHeightChange(id, fieldId, fieldIdentifier, fieldPositionId, changes);

    return 'applied';

  }, [id, rowHeight, onFieldAutoHeightChange]);

  const getFields = useCallback(() => {

    return getPageFieldPositions({
      fields,
      mode,
      presentation,
      fieldMeasurements: measurements
    });

  }, [fields, presentation, mode, measurements]);

  const layoutFields = getFields();

  /**
   * DOM Performance Enhancements
   * * Lazyloading should use the defined prop if it's defined. If not then default to lazyloading if fields are greater than 250.
   * * Drag preview should use the defined prop if it's defined. If not then default to lazyloading if fields are greater than 250.
   */
  const lazyloadFinal = typeof enableLazyLoadFields === 'boolean' ? enableLazyLoadFields : layoutFields.length > 250;
  const disableDragPreviewFinal = layoutFields.length > 250;

  const handleScroll = (e) => {

    //If lazyload is not enabled then we don't need scroll position tracking
    if (!lazyloadFinal) return;

    const current = e.target.scrollTop;
    const difference = Math.abs(current - scrollUpdated);
    /**
     * When a user has scrolled in either direction greater than
     * 100px then trigger the lazyload field position visbility check
     */
    if (difference > 10) setScrollUpdated(e.target.scrollTop);
  };

  return (
    <StyledWrapper
      {...{ width: containerWidth }}
      onScroll={handleScroll}
      ref={(e) => {
        if (!e) return;
        if (!scrollManagerRef) setScrollManagerRef(e);
      }}
    >
      <StyledBorderWrapper
        {...{
          border: pageBorder,
          margin,
          padding,
          height: adjustedContainerHeight
        }}
      >
        <FieldCreationDropLayerElement
          {...dropElementProps}
        >
          <StyledPageFields>
            <GridLayout
              {...props}
              config={config}
              parentScrollContainer={{
                scrollTop: scrollManagerRef ? scrollManagerRef.scrollTop : 0,
                clientHeight: scrollManagerRef ? scrollManagerRef.clientHeight : 0
              }}
              fields={layoutFields}
              width={adjustedPageWidth}
              cols={cols}
              enableLazyLoadFields={lazyloadFinal}
              disableDragPreview={disableDragPreviewFinal}
              enableFieldMeasurementRendering={measure}
              updateTimestamps={updateTimestamps}
              measurements={measurements}
              onFieldMeasurementCompleted={handleFieldMeasurementCompleted}
              onFieldAutoHeightMeasured={handleFieldAutoHeightMeasured}
            />
          </StyledPageFields>
        </FieldCreationDropLayerElement>
      </StyledBorderWrapper>
    </StyledWrapper>
  );

};

/**
 * What is 2 used for?
 *
 * We do (* 2) to represent the number of sides of the page to take into account.
 *
 * For instance if you want to adjust the height of the page by subtracting the margin
 * from top and bottom of the page then you would use a multiple of 2.
 */
const getAdjustedDimension = (value, padding, margin, borderWidth) => {

  let pageValue = value;
  if (padding) pageValue -= (padding * 2);
  if (borderWidth) pageValue -= (borderWidth * 2);
  if (margin) pageValue -= (margin * 2);

  return pageValue;

};

export const getMeasurableFieldSelector = createSelector(
  (state) => state.fields,
  (state) => state.licenseValidation,
  (fields, licenseValidation) => {
    return getMeasurableFields(fields, licenseValidation);
  }
);

export default memo(FormPage);
